import type { Row } from "./api";
import type { TaxTopicBinding } from "./tax-topics";
import type { InterviewPage } from "./interview-pages";
import type { ReturnProfile } from "./return-builder-model";
import { pageSchema, pageValue, mergePageValue } from "./interview-page-values";
import { mergeTopicEdit } from "./topic-save";
import { interviewItemLabels } from "./interview-copy";

const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));
const object = (value: unknown): value is Row =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const optionalDetails: Record<string, string[]> = {
  "INT.taxpayer.name": ["middle_name", "suffix"],
  "INT.taxpayer.contact": ["phone", "email"],
};
const labels: Record<string, string> = {
  first_name: "First name",
  middle_name: "Middle initial",
  last_name: "Last name",
  suffix: "Suffix",
  date_of_birth: "Date of birth",
  age_for_2025: "Age",
  blind: "Legally blind",
  phone: "Phone",
  email: "Email",
  claimable_status: "Someone else can claim this taxpayer",
  dependent_count: "Number of dependents",
  qualifying_person_count: "Other qualifying people",
};

/** Navigation relevance only, not tax eligibility or an answer to an omitted item. */
const itemProfileCards: Record<string, readonly string[]> = {
  "OI-BUSINESS": ["business", "gig"],
  "OI-RENTAL-K1": ["rental"],
  "OI-FARM": ["business"],
  "OI-HOME-SALE": ["homeowner"],
  "OI-COD": ["homeowner", "business", "gig", "investments", "rental"],
  "OI-UNREPORTED": [
    "employee",
    "business",
    "gig",
    "investments",
    "rental",
    "retirement",
  ],
  "AJ-HSA-MSA": ["employee"],
  "AJ-IRA": ["retirement", "business", "gig"],
  "AJ-SELF-EMPLOYED": ["business", "gig"],
  "S1A-TIPS": ["employee", "gig"],
  "S1A-OVERTIME": ["employee"],
  "S1A-VEHICLE": ["employee", "business", "gig"],
  "S1A-SENIOR": ["retirement"],
  "CR-EITC": ["employee", "business", "gig"],
  "CR-SAVER": ["employee", "retirement", "business", "gig"],
  "CR-ELDERLY-DISABLED": ["retirement"],
  "CR-FOREIGN": ["investments"],
  "CR-GENERAL-BUSINESS": ["business"],
};

/** A detached display projection; source bindings and hidden source answers are never changed. */
export function builderPageForProfile(
  page: InterviewPage,
  profile: ReturnProfile,
): InterviewPage | null {
  if (!page.itemIds || page.reviewOnly) return page;
  const selected = new Set(
    profile?.version === 1 &&
      profile.confirmed === true &&
      Array.isArray(profile.selected)
      ? profile.selected
      : [],
  );
  const itemIds = page.itemIds.filter(
    (id) =>
      !itemProfileCards[id] ||
      itemProfileCards[id].some((card) => selected.has(card)),
  );
  if (!itemIds.length) return null;
  return itemIds.length === page.itemIds.length ? page : { ...page, itemIds };
}

/** Select root data fields without recursively dropping children of a selected field. */
function projectSchemaFields(schema: Row, fields: string[]): void {
  if (object(schema.properties)) {
    if (schema.properties.state) {
      if (object(schema.properties.value))
        projectSchemaFields(schema.properties.value, fields);
    } else {
      schema.properties = Object.fromEntries(
        Object.entries(schema.properties).filter(([key]) =>
          fields.includes(key),
        ),
      );
      if (Array.isArray(schema.required))
        schema.required = schema.required.filter((key: string) =>
          fields.includes(key),
        );
    }
  }
  for (const keyword of ["anyOf", "oneOf", "allOf"]) {
    if (Array.isArray(schema[keyword]))
      for (const branch of schema[keyword])
        if (object(branch)) projectSchemaFields(branch, fields);
  }
  if (object(schema["x-intake-stateful"]?.valueSchema))
    projectSchemaFields(schema["x-intake-stateful"].valueSchema, fields);
}
export function builderSchema(
  page: InterviewPage,
  card: TaxTopicBinding,
  fields?: string[],
): Row {
  const schema = pageSchema(page, card);
  if (fields) projectSchemaFields(schema, fields);
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!object(node)) return;
    if (object(node.properties)) {
      for (const [key, field] of Object.entries(node.properties))
        if (object(field) && labels[key]) field["x-tax-label"] = labels[key];
      if (Array.isArray(node.required))
        node.required = node.required.filter(
          (key: string) =>
            !(optionalDetails[card.record.id] || []).includes(key),
        );
    }
    Object.values(node).forEach(walk);
  };
  walk(schema);
  return schema;
}
export function builderValue(
  page: InterviewPage,
  value: unknown,
  fields?: string[],
): unknown {
  const projected = pageValue(page, value);
  if (!fields || !object(projected)) return projected;
  const data = Object.hasOwn(projected, "state") ? projected.value : projected;
  if (!object(data)) return projected;
  const selected = Object.fromEntries(
    Object.entries(data).filter(([key]) => fields.includes(key)),
  );
  return Object.hasOwn(projected, "state")
    ? { ...projected, value: selected }
    : selected;
}
export function mergeBuilderValue(
  whole: unknown,
  before: unknown,
  next: unknown,
): unknown {
  return mergeTopicEdit(whole, before, next);
}
/** Use for profile-projected master pages so replacing a wrapper cannot erase hidden topics. */
export function mergeBuilderPageValue(
  page: InterviewPage,
  whole: unknown,
  before: unknown,
  next: unknown,
): unknown {
  return mergePageValue(page, whole, before, next);
}
/** Only on explicit Save: blank optional name/contact details use the original nullable representation. */
export function builderSaveValue(
  card: TaxTopicBinding,
  value: unknown,
): unknown {
  const result = clone(value);
  if (!object(result)) return result;
  const data = Object.hasOwn(result, "state")
    ? result.state === "KNOWN"
      ? result.value
      : undefined
    : result;
  if (!object(data)) return result;
  for (const key of optionalDetails[card.record.id] || [])
    if (data[key] === undefined || data[key] === "") data[key] = null;
  return result;
}
export function builderItemLabel(
  card: TaxTopicBinding,
  itemId: string,
): string {
  const find = (node: unknown): string | undefined => {
    if (!object(node)) return undefined;
    const item = node.properties?.items?.properties?.[itemId];
    const title =
      item?.["x-tax-label"] ?? item?.["x-intake-label"] ?? item?.title;
    if (typeof title === "string" && title.trim()) return title;
    const known = find(node["x-intake-stateful"]?.valueSchema);
    if (known) return known;
    const wrapped = find(node.properties?.value);
    if (wrapped) return wrapped;
    for (const keyword of ["anyOf", "oneOf", "allOf"])
      if (Array.isArray(node[keyword]))
        for (const branch of node[keyword]) {
          const label = find(branch);
          if (label) return label;
        }
    return undefined;
  };
  const title = find(card.schema) ?? interviewItemLabels[itemId];
  // Never render an opaque identifier, or silently give all topics the same generic label.
  if (!title)
    throw new Error(
      "The topic label is unavailable. Refresh the return before editing this section.",
    );
  return title.replace(/^Any\s+/i, "").replace(/\s+candidate$/i, "");
}

/** Concise topic titles. The full source label remains available for help and accessibility. */
export const builderItemTitles: Record<string, string> = {
  "RH-FILED": "Previously filed return",
  "RH-AMENDED": "Amended or superseding return",
  "RH-DECEDENT": "Deceased taxpayer",
  "RH-BANKRUPTCY": "Bankruptcy matters",
  "RH-RELIEF": "Special tax relief",
  "RH-REFUND-LIMIT": "Filing history and refund deadlines",
  "SC-MARRIAGE": "Marital status",
  "SC-SPOUSE-DEATH": "Death of a spouse",
  "SC-HOUSEHOLD": "Household filing circumstances",
  "SC-RDP-COMMUNITY": "Domestic partnership and community property",
  "SC-DEPENDENCY": "Dependents, support, and custody",
  "OI-INTEREST": "Interest income",
  "OI-DIVIDEND": "Dividend income",
  "OI-RETIREMENT": "IRA, pension, and retirement distributions",
  "OI-SS-RR": "Social Security and Railroad Retirement",
  "OI-CAPITAL-DIGITAL": "Investment sales and digital assets",
  "OI-BUSINESS": "Business, gig, and hobby income",
  "OI-RENTAL-K1": "Rental, royalty, and pass-through income",
  "OI-FARM": "Farm income",
  "OI-UNEMPLOYMENT-ALIMONY-REFUND": "Unemployment, state refunds, and alimony",
  "OI-GAMBLING-OTHER": "Gambling, prizes, and other income",
  "OI-COD": "Canceled debt and foreclosure",
  "OI-FOREIGN": "Foreign income",
  "OI-GIFT-INHERIT-LIFE": "Gifts, inheritances, and life insurance",
  "OI-HOME-SALE": "Home sale or exchange",
  "OI-UNREPORTED": "Income and transactions without a tax form",
  "WB-TIPS": "Tips",
  "WB-OVERTIME": "Overtime pay",
  "WB-SICK-PAY": "Third-party sick pay",
  "WB-STATUTORY": "Statutory employee wages",
  "WB-EQUITY": "Stock options and equity compensation",
  "WB-FOREIGN-EXCLUSION": "Foreign income and housing exclusions",
  "WB-EMPLOYER-EDUCATION": "Education assistance and scholarships",
  "WB-OTHER-FRINGE": "Other benefits and special wage treatment",
  "AJ-EDUCATOR": "Educator expenses",
  "AJ-HSA-MSA": "Health savings and medical savings accounts",
  "AJ-SELF-EMPLOYED": "Self-employment adjustments",
  "AJ-IRA": "IRA contributions and recharacterizations",
  "AJ-STUDENT-LOAN": "Student loan interest",
  "AJ-OTHER": "Other adjustments to income",
  "S1A-COMMON": "Additional deduction review",
  "S1A-TIPS": "Tip deduction review",
  "S1A-OVERTIME": "Overtime deduction review",
  "S1A-VEHICLE": "Passenger vehicle loan interest",
  "S1A-SENIOR": "Senior deduction review",
  "IT-MEDICAL": "Medical and dental expenses",
  "IT-SALT": "State and local taxes",
  "IT-INTEREST": "Mortgage and investment interest",
  "IT-CHARITY-CASH": "Cash charitable contributions",
  "IT-CHARITY-NONCASH": "Donated property and noncash contributions",
  "IT-CASUALTY": "Casualty, theft, and disaster losses",
  "IT-OTHER": "Other itemized expenses and adjustments",
  "IT-CARRYOVER": "Charitable and itemized deduction carryovers",
  "CR-CTC-ODC": "Child and other dependent credits",
  "CR-EITC": "Earned income credit review",
  "CR-DEPENDENT-CARE": "Child, dependent, and spouse care credit",
  "CR-AOC": "American opportunity credit review",
  "CR-LLC": "Lifetime learning credit review",
  "CR-SAVER": "Retirement savings credit review",
  "CR-PTC": "Marketplace coverage and premium tax credit",
  "CR-FOREIGN": "Foreign taxes and foreign tax credit",
  "CR-ENERGY": "Home energy improvements",
  "CR-CLEAN-VEHICLE": "New, used, and commercial clean vehicles",
  "CR-ELDERLY-DISABLED": "Credit for the elderly or disabled",
  "CR-ADOPTION": "Adoption expenses and credit",
  "CR-OTHER-NONREF": "Other nonrefundable credits",
  "CR-OTHER-REF": "Other refundable credits",
  "CR-GENERAL-BUSINESS": "Business credits and carryforwards",
  "OT-SELF-EMPLOYMENT": "Self-employment tax review",
  "OT-ADDITIONAL-MEDICARE": "Additional Medicare and unreported tip taxes",
  "OT-NIIT": "Net investment income tax review",
  "OT-HOUSEHOLD": "Household employment taxes",
  "OT-RETIREMENT-HSA": "Retirement, savings, and education account taxes",
  "OT-RECAPTURE-SPECIAL": "Recapture and other special taxes",
  "PY-ESTIMATED": "Estimated tax payments",
  "PY-EXTENSION": "Extension payment",
  "PY-PRIOR-APPLIED": "Prior-year refund applied to this year",
  "PY-OTHER": "Other payments, refundable credits, and withholding claims",
  "CE-NOL": "Net operating loss carryover",
  "CE-CAPITAL": "Capital loss carryover",
  "CE-PASSIVE": "Passive activity and at-risk carryovers",
  "CE-ELECTION": "Tax elections and accounting methods",
  "CE-INJURED-INNOCENT": "Joint-return liability and allocation relief",
  "ITL-RESIDENCY": "International residence and treaty matters",
  "ITL-ASSET": "Foreign accounts, assets, and reporting",
  "ITL-TERRITORY": "U.S. territory income and residence",
  "XD-1099-1098": "Income, deduction, and brokerage statements",
  "XD-1095A-SSA-RRB-K1": "Health coverage, benefits, and K-1 statements",
  "XD-OTHER": "Other tax documents",
};

export function builderItemTitle(
  card: TaxTopicBinding,
  itemId: string,
): string {
  return builderItemTitles[itemId] ?? builderItemLabel(card, itemId);
}
